import { requestConclusionResult } from 'dashboard/composables/useConclusionResultPrompt';
import { ref, unref } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';
import wootConstants from 'dashboard/constants/globals';
import ConversationApi from 'dashboard/api/inbox/conversation';

export function useBulkActions() {
  const store = useStore();
  const { t } = useI18n();
  const { checkMissingAttributes } = useConversationRequiredAttributes();

  const selectedConversations = useMapGetter(
    'bulkActions/getSelectedConversationIds'
  );
  const selectedInboxes = ref([]);

  function selectConversation(conversationId, inboxId) {
    store.dispatch('bulkActions/setSelectedConversationIds', conversationId);
    selectedInboxes.value = [...selectedInboxes.value, inboxId];
  }

  function deSelectConversation(conversationId, inboxId) {
    store.dispatch('bulkActions/removeSelectedConversationIds', conversationId);
    selectedInboxes.value = selectedInboxes.value.filter(
      item => item !== inboxId
    );
  }

  function resetBulkActions() {
    store.dispatch('bulkActions/clearSelectedConversationIds');
    selectedInboxes.value = [];
  }

  function selectAllConversations(check, conversationList) {
    const availableConversations = unref(conversationList);
    if (check) {
      store.dispatch(
        'bulkActions/setSelectedConversationIds',
        availableConversations.map(item => item.id)
      );
      selectedInboxes.value = availableConversations.map(item => item.inbox_id);
    } else {
      resetBulkActions();
    }
  }

  function isConversationSelected(id) {
    return selectedConversations.value.includes(id);
  }

  // Same method used in context menu, conversationId being passed from there.
  //
  // `onRequiresTransferRequest` reproduz aqui o que o menu do header já fazia
  // em MoreActions.transferTo: quando o backend barra a transferência direta
  // (403 `requires_transfer_request`), a tela não anuncia falha — conduz o
  // vendedor ao caminho que ele de fato tem, que é abrir a solicitação para o
  // gestor. Sem isso o menu do cartão terminava num alerta seco.
  async function onAssignAgent(
    agent,
    conversationId = null,
    { onRequiresTransferRequest = null } = {}
  ) {
    const ids = conversationId || selectedConversations.value;
    const isSingleConversationAssignment =
      conversationId !== null &&
      conversationId !== undefined &&
      Array.isArray(ids) &&
      ids.length === 1;

    try {
      // The context menu passes one conversation id, while the bulk toolbar
      // passes the selected list. Keep a single conversation on the same
      // synchronous path used by the conversation Actions tab so the API
      // response confirms the assignee before showing success.
      if (isSingleConversationAssignment) {
        // A lista é atualizada assim que a API confirma, sem esperar a
        // segunda requisição (a dos contadores) — que era o atraso visível
        // que a atualização otimista existia para esconder.
        //
        // A escrita otimista rodava ANTES do POST e tirava a conversa da caixa
        // na hora; quando o backend recusava a transferência direta, o cartão
        // já tinha sido destruído junto com o modal de solicitação que vive
        // dentro dele, e o vendedor ficava sem nenhuma saída na tela.
        const response = await ConversationApi.assignAgent({
          conversationId: ids[0],
          agentId: agent.id,
        });

        // Transferência que exige aprovação ainda não foi aplicada no
        // servidor: o responsável na tela continua sendo o atual.
        if (response.data?.type !== 'pending_transfer_request') {
          store.dispatch('setCurrentChatAssignee', {
            conversationId: ids[0],
            assignee: response.data,
          });
          store
            .dispatch('conversationStats/get', { immediate: true })
            .catch(() => {});
        }

        store.dispatch('bulkActions/clearSelectedConversationIds');
        useAlert(
          t('CONVERSATION.CARD_CONTEXT_MENU.API.AGENT_ASSIGNMENT.SUCCESFUL', {
            agentName: agent.name,
            conversationId: ids[0],
          })
        );
        return;
      }

      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids,
        fields: {
          assignee_id: agent.id,
        },
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      if (conversationId) {
        useAlert(
          t('CONVERSATION.CARD_CONTEXT_MENU.API.AGENT_ASSIGNMENT.SUCCESFUL', {
            agentName: agent.name,
            conversationId,
          })
        );
      } else {
        useAlert(t('BULK_ACTION.ASSIGN_SUCCESFUL'));
      }
    } catch (err) {
      if (
        err?.response?.data?.requires_transfer_request &&
        onRequiresTransferRequest
      ) {
        await onRequiresTransferRequest(agent);
        return;
      }
      useAlert(err?.response?.data?.error || t('BULK_ACTION.ASSIGN_FAILED'));
    }
  }

  // Same method used in context menu, conversationId being passed from there.
  async function onAssignLabels(newLabels, conversationId = null) {
    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids: conversationId || selectedConversations.value,
        labels: {
          add: newLabels,
        },
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      if (conversationId) {
        useAlert(
          t('CONVERSATION.CARD_CONTEXT_MENU.API.LABEL_ASSIGNMENT.SUCCESFUL', {
            labelName: newLabels[0],
            conversationId,
          })
        );
      } else {
        useAlert(t('BULK_ACTION.LABELS.ASSIGN_SUCCESFUL'));
      }
    } catch (err) {
      useAlert(t('BULK_ACTION.LABELS.ASSIGN_FAILED'));
    }
  }

  // Only used in context menu
  async function onRemoveLabels(labelsToRemove, conversationId = null) {
    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids: conversationId || selectedConversations.value,
        labels: {
          remove: labelsToRemove,
        },
      });

      useAlert(
        t('CONVERSATION.CARD_CONTEXT_MENU.API.LABEL_REMOVAL.SUCCESFUL', {
          labelName: labelsToRemove[0],
          conversationId,
        })
      );
    } catch (err) {
      useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.LABEL_REMOVAL.FAILED'));
    }
  }

  async function onAssignTeamsForBulk(team) {
    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids: selectedConversations.value,
        fields: {
          team_id: team.id,
        },
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      useAlert(t('BULK_ACTION.TEAMS.ASSIGN_SUCCESFUL'));
    } catch (err) {
      useAlert(t('BULK_ACTION.TEAMS.ASSIGN_FAILED'));
    }
  }

  async function onUpdateConversations(status, snoozedUntil) {
    let conversationIds = selectedConversations.value;
    let skippedCount = 0;

    // If resolving, check for required attributes
    if (status === wootConstants.STATUS_TYPE.RESOLVED) {
      const { validIds, skippedIds } = selectedConversations.value.reduce(
        (acc, id) => {
          const conversation = store.getters.getConversationById(id);
          const currentCustomAttributes = conversation?.custom_attributes || {};
          const { hasMissing } = checkMissingAttributes(
            currentCustomAttributes
          );

          if (!hasMissing) {
            acc.validIds.push(id);
          } else {
            acc.skippedIds.push(id);
          }
          return acc;
        },
        { validIds: [], skippedIds: [] }
      );

      conversationIds = validIds;
      skippedCount = skippedIds.length;

      if (skippedCount > 0 && validIds.length === 0) {
        // All conversations have missing attributes
        useAlert(
          t('BULK_ACTION.RESOLVE.ALL_MISSING_ATTRIBUTES') ||
            'Cannot resolve conversations due to missing required attributes'
        );
        return true;
      }
    }

    // Aceleres: concluir em massa pede o tipo de conclusão, aplicado a todas
    // as conversas selecionadas. Cancelar não conclui (retorna false).
    let conclusionResult = null;
    if (
      status === wootConstants.STATUS_TYPE.RESOLVED &&
      conversationIds.length > 0
    ) {
      conclusionResult = await requestConclusionResult({
        count: conversationIds.length,
      });
      if (!conclusionResult) return false;
    }

    try {
      if (conversationIds.length > 0) {
        await store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: conversationIds,
          fields: {
            status,
          },
          snoozed_until: snoozedUntil,
          ...(conclusionResult ? { conclusion_result: conclusionResult } : {}),
        });

        // A caixa do atendimento é decidida pelo status que está no store: a
        // lista esconde de Novos/Meus/Outros/Pessoais tudo que está
        // `resolved`. Sem esta escrita a conversa concluída em massa
        // continuava visível até o broadcast do job chegar (5,6 s medidos) —
        // ou indefinidamente, se ele se perdesse.
        store.dispatch('applyConversationStatusLocally', {
          conversationIds,
          status,
          snoozedUntil,
        });
        // Os contadores das abas vêm de outro endpoint e não acompanham a
        // escrita local; o caminho de uma conversa só (`toggleStatus`) já
        // pedia os totais de novo aqui.
        store
          .dispatch('conversationStats/get', { immediate: true })
          .catch(() => {});
      }

      store.dispatch('bulkActions/clearSelectedConversationIds');

      if (skippedCount > 0) {
        useAlert(t('BULK_ACTION.RESOLVE.PARTIAL_SUCCESS'));
      } else {
        useAlert(t('BULK_ACTION.UPDATE.UPDATE_SUCCESFUL'));
      }
    } catch (err) {
      useAlert(t('BULK_ACTION.UPDATE.UPDATE_FAILED'));
    }
    return true;
  }

  return {
    selectedConversations,
    selectedInboxes,
    selectConversation,
    deSelectConversation,
    selectAllConversations,
    resetBulkActions,
    isConversationSelected,
    onAssignAgent,
    onAssignLabels,
    onRemoveLabels,
    onAssignTeamsForBulk,
    onUpdateConversations,
  };
}
